import { tabbable } from 'tabbable'
import {
    createEvent,
    isScrollingTo,
    scrollTo,
    setSiblingsAriaHidden
} from '../utils/dom'

export default class Navbar {
    constructor () {
        // Get the navbar wrapper element
        this.navbarWrapper = document.querySelector('.navbar-wrapper')

        // If there's no navbarWrapper, return
        if (!this.navbarWrapper)
            return

        // Don't run for small navbars
        if (this.navbarWrapper.classList.contains('navbar-wrapper--small'))
            return

        // Attrs/identifiers
        this.drodopwnAttr = 'data-dropdown-expanded'

        this.searchAttr = 'data-search-expanded'

        this.stickyAttr = 'data-sticky'

        // Get the navbar element
        this.navbar = document.querySelector('.navbar')

        // Get the Kanga sticky navbar
        this.navbarKanga = document.querySelector('[data-global-kanga-navbar]')

        // Clone Navbar for consistent height during sticky toggling
        this.cloneNavBar()

        // Get items that has submenu
        this.menuChildren = Array.from(this.navbarWrapper
            .querySelectorAll('.menu-item-has-children'))

        this.menuOpenCss = 'menu-item-open'

        // Search Btn & Input
        this.searchBtns = Array.from(this.navbarWrapper
            .querySelectorAll('a.gtm-nav-btn-search'))

        this.listen()

        this.scrollProgress()
    }

    listen () {
        // Watch click/hover/leave events to show/hide dropdown areas
        this.menuChildren
            .forEach(menu => {
                menu.addEventListener(
                    'click',
                    this.dropdownToggle.bind(this)
                )

                menu.addEventListener(
                    'mouseenter',
                    this.dropdownToggle.bind(this)
                )

                menu.addEventListener(
                    'mouseleave',
                    this.dropdownToggle.bind(this)
                )
            })

        this.searchBtns
            .forEach(el => {
                el.setAttribute('role', 'button')
                el.setAttribute('aria-label', 'Toggle search form')
                el.addEventListener('click', this.searchToggle.bind(this))
            })

        this.navbarWrapper
            .addEventListener('click', (e) => {
                if (e.target !== this.navbarWrapper
                        || (!this.isDropdownExpanded
                            && !this.isSearchExpanded()))
                    return

                this.dropdownCloseAll()

                this.searchClose()

                this.emitScrollEnable()
            })

        this.navbarWrapper
            .addEventListener('touchmove', this.onTouchMove.bind(this), false)

        // Watch for scroll events to determine if show/hide the sticky navbarWrapper
        document
            .addEventListener('scroll', this.onScroll.bind(this), false)

        document
            .addEventListener('keydown', this.onDocumentKeyDown.bind(this))

        window
            .addEventListener('resize', this.onResize.bind(this))

        window
            .addEventListener('scroll:enable', this.onScrollEnable.bind(this))

        window
            .addEventListener('scroll:disable', this.onScrollDisable.bind(this))
    }

    emitScrollEnable () {
        const event = createEvent('scroll:enable', {
            bubbles: false,
            cancelable: true,
            detail: {
                dispatcher: this.navbarWrapper
            }
        })

        window.dispatchEvent(event)
    }

    emitScrollDisable () {
        const event = createEvent('scroll:disable', {
            bubbles: false,
            cancelable: true,
            detail: {
                dispatcher: this.navbarWrapper
            }
        })

        window.dispatchEvent(event)
    }

    onScroll () {
        if (!document.documentElement.classList.contains('overflow-hidden'))
            this.scrollProgress()
    }

    onTouchMove (e) {
        if (!document.documentElement.classList.contains('overflow-hidden')
                || e.target !== this.navbarWrapper)
            return

        e.preventDefault()
        e.stopPropagation()
    }

    onScrollEnable (e) {
        if (e.detail?.dispatcher !== this.navbarWrapper)
            return

        setSiblingsAriaHidden(this.navbarWrapper, false)

        this.navbarWrapper.removeAttribute('aria-role')
        this.navbarWrapper.removeAttribute('aria-modal')
    }

    onScrollDisable (e) {
        if (e.detail?.dispatcher !== this.navbarWrapper)
            return

        setSiblingsAriaHidden(this.navbarWrapper)

        this.navbarWrapper.setAttribute('aria-role', 'dialog')
        this.navbarWrapper.setAttribute('aria-modal', 'true')
    }

    onDocumentKeyDown (e) {
        const key = (e.key || e.keyIdentifier || '')

        if (key.match(/escape/i)
                && (this.isDropdownExpanded()
                    || this.isSearchExpanded())) {
            this.dropdownCloseAll()
            this.searchClose()
            this.emitScrollEnable()
        }

        if (!key.match(/tab/i)
                || this.navbarWrapper.getAttribute('aria-role') !== 'dialog'
                || document.body.classList.contains('modal-open'))
            return

        let focusEl

        let els = (tabbable(this.navbarWrapper) || [])

        if (this.isSearchExpanded()) {
            const searchBtn = this.searchBtns
                .filter(el => els.indexOf(el) >= 0)[0]

            const isSearchFormEl = (el) => {
                if (!searchBtn
                        || !searchBtn.getAttribute('aria-controls'))
                    return false

                const controlsEl = document.getElementById(searchBtn
                    .getAttribute('aria-controls'))

                return controlsEl
                    && controlsEl.contains(el)
            }

            const searchFormEls = els
                .filter(el => isSearchFormEl(el))

            els = els
                .filter(el => !isSearchFormEl(el))

            const elsSplitIdx = searchBtn
                ? els.indexOf(searchBtn) + 1
                : null

            const elsStart = elsSplitIdx
                ? els.slice(0, elsSplitIdx)
                : null

            const elsEnd = elsSplitIdx
                ? els.slice(elsSplitIdx)
                : null

            els = elsStart && elsEnd
                ? elsStart
                    .concat(searchFormEls)
                    .concat(elsEnd)
                : els

            const isActiveSearchBtn = searchBtn === e.target

            const isActiveSearchInput = e.target === this.getSearchInput()

            const isActiveSearchSubmit = !isActiveSearchInput
                && searchFormEls.indexOf(e.target) >= 0

            if (isActiveSearchBtn
                    && !e.shiftKey)
                focusEl = els[els.indexOf(searchBtn) + 1]

            if (isActiveSearchInput
                    && e.shiftKey)
                focusEl = els[els.indexOf(e.target) - 1]

            if (isActiveSearchSubmit
                    && !e.shiftKey)
                focusEl = els[els.indexOf(e.target) + 1]
        }

        if (e.shiftKey
                && els.indexOf(e.target) === 0)
            focusEl = els[els.length - 1]

        if (!e.shiftKey
                && els.indexOf(e.target) === els.length - 1)
            focusEl = els[0]

        if (!focusEl)
            return

        e.preventDefault()
        e.stopPropagation()

        focusEl.focus({ preventScroll: true })
    }

    onResize (e) {
        if (this.isSmallViewport()
                && this.isDropdownExpanded()
                && this.isSticky())
            return this.dropdownCloseAll()

        if (!this.isSmallViewport()
                && this.isDropdownExpanded()
                && this.navbarWrapper
                    .getAttribute('aria-role') === 'dialog')
            this.emitScrollEnable()

        if (this.isSmallViewport()
                && this.isDropdownExpanded()
                && this.navbarWrapper
                    .getAttribute('aria-role') !== 'dialog')
            this.emitScrollDisable()
    }

    cloneNavBar () {
        if (this.getStickyNav()
                && this.getStickyNav() === this.navbarKanga)
            return

        const clone = this.navbar.cloneNode(true)

        clone.classList
            .add('navbar-clone')

        clone.classList
            .add('navbar-sticky')

        Array.from(clone.querySelectorAll('[id]'))
            .forEach((el) => {
                const controls = Array.from(clone
                    .querySelectorAll(`[aria-controls=${el.id}]`))

                const owns = Array.from(clone
                    .querySelectorAll(`[aria-owns=${el.id}]`))

                const labelsFor = Array.from(clone
                    .querySelectorAll(`[for=${el.id}]`))

                el.id = `${el.id}-clone`

                controls
                    .forEach(_el => _el.setAttribute('aria-controls', el.id))

                owns
                    .forEach(_el => _el.setAttribute('aria-owns', el.id))

                labelsFor
                    .forEach(_el => _el.setAttribute('for', el.id))
            })

        const navbarTop = clone.querySelector('.navbar-top-wrapper')

        if (navbarTop)
            navbarTop.parentElement
                .appendChild(navbarTop)

        this.navbarWrapper
            .appendChild(clone)
    }

    getStickyNav () {
        return this.navbarKanga
            || this.navbarWrapper.querySelector('.navbar-sticky')
    }

    getStickyNavOffsetY () {
        return this.navbarWrapper.offsetHeight
    }

    isSticky () {
        const el = this.getStickyNav() === this.navbarKanga
            ? this.navbarKanga
            : this.navbarWrapper

        return el
            && el.getAttribute(this.stickyAttr) === 'true'
    }

    getScrollY () {
        return window.scrollY || window.pageYOffset
    }

    scrollProgress () {
        const scrollY = window.scrollY || window.pageYOffset

        const isSticky = this.isSticky()

        const el = this.getStickyNav() === this.navbarKanga
            ? this.navbarKanga
            : this.navbarWrapper

        const stickyOffsetY = this.getStickyNavOffsetY()

        if (!el
                || (scrollY >= stickyOffsetY && isSticky)
                || (scrollY < stickyOffsetY && !isSticky))
            return

        this.dropdownCloseAll()

        this.searchClose()

        el.setAttribute(this.stickyAttr, !isSticky)
    }

    isSmallViewport () {
        return window.innerWidth < 992
    }

    isDropdownExpanded () {
        return this.navbarWrapper
            .getAttribute(this.drodopwnAttr) === 'true'
    }

    dropdownToggle (e) {
        const el = e.target
            .closest('.menu-item')

        if (isScrollingTo()
                || (el.getAttribute('data-dropdown-expanding') === 'true'
                    && e.type === 'mouseenter')
                || (this.isSearchExpanded()
                    && e.type !== 'click')
                || (this.isSmallViewport()
                    && e.type === 'mouseenter'))
            return

        const show = !el.classList.contains(this.menuOpenCss)
            && e.type !== 'mouseleave'

        el
            .setAttribute('data-dropdown-expanding', show)

        if (show) {
            this.dropdownCloseAll()
            this.searchClose()

            if (!this.isSmallViewport()
                    && this.navbarWrapper.getAttribute('aria-role'))
                this.emitScrollEnable()
        }

        const setAttrs = () => {
            el.classList[show
                ? 'add'
                : 'remove'
            ](this.menuOpenCss)

            el.querySelector('a, button')
                ?.setAttribute('aria-expanded', show)

            this.navbarWrapper
                .setAttribute(this.drodopwnAttr, show)

            if (this.isSmallViewport())
                show
                    ? this.emitScrollDisable()
                    : this.emitScrollEnable()

            setTimeout(() => el
                .removeAttribute('data-dropdown-expanding'), 400)
        }

        return this.isSmallViewport()
                && !this.isSticky()
                && this.getScrollY()
                && show
            ? scrollTo([0, 0])
                .then(setAttrs)
            : Promise.resolve(setAttrs())
    }

    dropdownCloseAll () {
        const els = this.navbarWrapper.querySelectorAll(`.${this.menuOpenCss}`)

        if (!els)
            return

        els.forEach(el => {
            el.querySelector('a, button')
                ?.setAttribute('aria-expanded', false)

            el.classList.remove(this.menuOpenCss)
        })

        this.navbarWrapper.setAttribute('data-dropdown-expanded', false)
    }

    isSearchPage () {
        return document.body.classList.contains('search')
    }

    isSearchExpanded () {
        return this.navbarWrapper
            .getAttribute(this.searchAttr) === 'true'
    }

    getSearchInput () {
        return this.isSearchPage()
            ? document.querySelector('main input[type=search]')
            : (this.isSticky()
                ? this.getStickyNav()
                : this.navbar).querySelector('input[type=search]')
    }

    searchToggle (e) {
        if (e) {
            e.preventDefault()
            e.stopPropagation()
        }

        const focusSearchInput = () => this.getSearchInput()
            && this.getSearchInput()
                .focus({ preventScroll: true })

        // Scroll to main input on Search Page
        if (this.isSearchPage())
            return scrollTo(this.getSearchInput(), {
                speed: 800
            })

        // Toggle expanded state on non-Search Page
        const show = !this.isSearchExpanded()

        if (show)
            this.dropdownCloseAll()

        const setSearchAttrs = () => new Promise((resolve, reject) => {
            const setAttrs = () => {
                this.navbarWrapper
                    .setAttribute(this.searchAttr, show)

                const searchBtn = this.searchBtns
                    .filter(el => el === e.target)[0]

                if (searchBtn)
                    searchBtn.setAttribute('aria-expanded', show)
            }

            resolve(show
                    && this.getScrollY()
                    && !this.isSticky()
                ? scrollTo([0, 0])
                    .then(setAttrs)
                : setAttrs())
        })

        return setSearchAttrs()
            .then(() => {
                show
                    ? this.emitScrollDisable()
                    : this.emitScrollEnable()

                if (show)
                    focusSearchInput()
            })
    }

    searchClose () {
        if (!this.isSearchExpanded())
            return

        this.navbarWrapper
            .setAttribute(this.searchAttr, false)

        this.searchBtns
            .forEach(el => el.setAttribute('aria-expanded', false))
    }
}
